import { cn } from "@/lib/utils";

export interface ResourceUsage {
  used: number;
  limit: number;
  unlimited: boolean;
}

interface UsageMeterProps {
  label: string;
  sublabel?: string;
  usage: ResourceUsage;
  /** Shown below the bar when usage exceeds the limit */
  overLimitHint?: string;
  /** Custom formatter for displayed used / limit values */
  formatValue?: (val: number) => string;
}

export function UsageMeter({
  label,
  sublabel,
  usage,
  overLimitHint,
  formatValue,
}: UsageMeterProps) {
  const displayUsed = formatValue ? formatValue(usage.used) : usage.used;
  const displayLimit = formatValue ? formatValue(usage.limit) : usage.limit;

  if (usage.unlimited) {
    return (
      <div className="flex items-center justify-between py-2">
        <div className="text-sm">
          <span className="font-medium">{label}</span>
          {sublabel && (
            <span className="ml-1.5 text-xs text-muted-foreground">
              ({sublabel})
            </span>
          )}
        </div>
        <span className="text-sm text-muted-foreground tabular-nums">
          {displayUsed} used
        </span>
      </div>
    );
  }

  const percent =
    usage.limit > 0 ? Math.min((usage.used / usage.limit) * 100, 100) : 100;
  const isWarning = percent >= 80;
  const isCritical = percent >= 100;
  const isOverLimit = usage.used > usage.limit;

  return (
    <div className="py-2">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-sm">
          <span className="font-medium">{label}</span>
          {sublabel && (
            <span className="ml-1.5 text-xs text-muted-foreground">
              ({sublabel})
            </span>
          )}
        </div>
        <span
          className={cn(
            "text-sm tabular-nums",
            isCritical
              ? "text-destructive font-medium"
              : isWarning
                ? "text-severity-important"
                : "text-muted-foreground",
          )}
        >
          {displayUsed}
          <span className="mx-0.5 text-muted-foreground">/</span>
          {displayLimit}
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-border"
        role="progressbar"
        aria-valuenow={usage.used}
        aria-valuemin={0}
        aria-valuemax={usage.limit}
        aria-label={`${label}: ${usage.used} of ${usage.limit}`}
      >
        <div
          className={cn(
            "h-full rounded-full transition-all duration-500",
            isCritical
              ? "bg-destructive"
              : isWarning
                ? "bg-mark-important"
                : "bg-primary",
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
      {isOverLimit && overLimitHint && (
        <p className="mt-1.5 text-xs text-destructive">{overLimitHint}</p>
      )}
    </div>
  );
}
